// src/utils/firestoreUtils.js
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, writeBatch } from "firebase/firestore";
import { db } from "../../firebase"; // import db from your firebase initialization file
import { getCurrentUserUid } from "../auth/authUtils";
import { showMessageWithDelay } from "../alertUtils";
import { getCurrentTimestamp, getEndDateTimestamp } from "../timeUtils";
import { v4 as uuidv4 } from 'uuid';

/**
 * Adds a user's email to the waitlist in Firestore if it is valid and not already present.
 *
 * This function:
 * 1. Validates the email format using a regex pattern.
 * 2. Checks if the email is already in the Firestore document `/StatData/initWaitlist`.
 * 3. If the email is new, it adds it to the `waitlistEmails` array field.
 * 4. Provides user feedback via `showMessageWithDelay` for success, errors, and duplicate entries.
 *
 * @param {string} email - The email entered by the user.
 * @param {function} setEmail - State setter function to clear the email input after a successful submission.
 *
 * @returns {Promise<void>} - A promise that resolves when the operation is complete.
 */
export const joinWaitlist = async (email, setEmail) => {
  // Function to validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  if (!isValidEmail(email)) {
    showMessageWithDelay(
      "Invalid Email",
      "Please enter a valid email.",
      "error",
      3000
    );
    return;
  }

  try {
    const waitlistRef = doc(db, "StatData", "initWaitlist");
    const waitlistSnap = await getDoc(waitlistRef);

    if (waitlistSnap.exists()) {
      const waitlistData = waitlistSnap.data();
      const currentEmails = waitlistData.waitlistEmails || [];

      if (currentEmails.includes(email)) {
        showMessageWithDelay(
          "Thank you!",
          "Looks like you are already supporting us! Thank you for helping us shape the future of climbing!",
          "success",
          3000
        );
        return;
      }
    }

    // Add email to Firestore if it's not already in the list
    await updateDoc(waitlistRef, {
      waitlistEmails: arrayUnion(email),
    });

    showMessageWithDelay(
      "Waitlist Joined!",
      "Thank you for joining our waitlist! Your support helps us shape the future of climbing!",
      "success",
      3000
    );
    setEmail("");
  } catch (error) {
    console.error("Error adding email to waitlist:", error);
    showMessageWithDelay(
      "Oh no!",
      "An error occurred trying to process your request, please try again later.",
      "error",
      3000
    );  }
};


/**
 * Function to fetch the list of all certification IDs stored in the 'CertificationInfo' collection.
 * @returns {Array} - An array of certification IDs or an empty array if none found.
 */
export const getCertificationIDList = async () => {
  try {
    // Reference to the CertificationInfo document
    const certDocRef = doc(db, "CertificationData", "CertificationInfo");

    // Fetch the document snapshot
    const certDocSnap = await getDoc(certDocRef);

    if (certDocSnap.exists()) {
      // Extract the certification ID list from the document field (e.g., certificationIDList)
      const certificationIDList = certDocSnap.data().certificationIDList;

      if (certificationIDList && Array.isArray(certificationIDList)) {
        return certificationIDList; // Return the certification ID list
      } else {
        return []; // Return an empty array if no certification ID list is found
      }
    } else {
      return []; // Return an empty array if the document doesn't exist
    }
  } catch (error) {
    return []; // Return an empty array in case of an error
  }
};

/**
 * Function to fetch the certification details based on the certification IDs.
 * @param {Array} certificationIDs - An array of certification IDs to fetch.
 * @returns {Array} - An array of certification details with ID and name.
 */
export const getCertificationsDetails = async (certificationIDs) => {
  try {
    const certifications = [];

    for (const certId of certificationIDs) {
      const certDocRef = doc(db, `CertificationData/CertificationInfo/CertificationIndex`, certId);
      const certDocSnap = await getDoc(certDocRef);

      if (certDocSnap.exists()) {
        const certData = certDocSnap.data();
        certifications.push({
          id: certId,
          name: certData.name, // Assuming the name is stored in the 'name' field
          type: certData.type,
        });
      } else {
      }
    }

    return certifications; // Return the certifications data
  } catch (error) {
    return []; // Return an empty array in case of an error
  }
};

// Function to get certifications based on the certification IDs stored in the user's document
export const getUserCertifications = async (userID) => {
  const user = userID;

  if (user) {
    try {
      // Reference to the user document
      const userDocRef = doc(db, "UserProfiles", user);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        // Get the array of certification IDs from the user document
        const certIds = userDocSnap.data().myCertifications;

        if (certIds && certIds.length > 0) {
          const certifications = [];

          // Loop through each certification ID and fetch data from the CertificationInfo subcollection
          for (const certId of certIds) {
            const certDocRef = doc(db, "UserProfiles", user, "CertificationInfo", certId);
            const certDocSnap = await getDoc(certDocRef);

            if (certDocSnap.exists()) {
              certifications.push(certDocSnap.data());
            } else {
            }
          }

          return certifications;  // Return the certifications data
        } else {
          return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  } else {
    return [];
  }
};

/**
 * Function to fetch the list of gym names from the "GymProfiles/AllGyms" document.
 * @returns {Array} - An array of gym names or references from Firestore.
 */
export const getGymList = async () => {
  try {
    // Get a reference to the "AllGyms" document
    const allGymsDocRef = doc(db, `Gym_Data_Public/AllGyms`);

    // Fetch the document snapshot
    const allGymsDocSnap = await getDoc(allGymsDocRef);

    if (allGymsDocSnap.exists()) {
      // Extract the gyms list from the document's field (e.g., gymsList)
      const gymsList = allGymsDocSnap.data().allGymsList;

      if (gymsList && Array.isArray(gymsList)) {
        return gymsList; // Return the gyms list
      } else {
        return []; // Return an empty array if no gyms list is found
      }
    } else {
      return []; // Return an empty array if the document doesn't exist
    }
  } catch (error) {
    return []; // Return an empty array in case of an error
  }
};

/**
 * Function to get the location data of a specific gym from Firestore.
 * @param {string} gymName - The name of the gym to fetch the location data for.
 * @returns {Object | null} - Returns the location data for the gym if found, otherwise null.
 */
export const getGymLocationData = async (gymNames) => {
  try {
    const gymsWithLocationData = [];

    for (let gymName of gymNames) {
      const gymDocRef = doc(db, `Gym_Data_Public/AllGyms/${gymName}/LocationData`);
      try {
        const gymDocSnap = await getDoc(gymDocRef);

        if (gymDocSnap.exists()) {
          const gymLocation = gymDocSnap.data(); // Assuming this contains the location data
          // Example: If locationData is a string and you need to split it
          if (gymLocation && typeof gymLocation.someField === 'string') {
            const locationArray = gymLocation.someField.split(","); // Example: splitting an address or coordinates
            gymsWithLocationData.push({
              gymName,
              locationData: locationArray,
            });
          } else {
            // Handle if it's not a string
            gymsWithLocationData.push({
              gymName,
              locationData: gymLocation, // If it's already structured, just pass it through
            });
          }
        }
      }
      catch (err) {
      }
    }
    return gymsWithLocationData;
  } catch (error) {
    return [];
  }
};

// Function to get 'title' and 'body' of News from Firestore document
export const getNewsFields = async () => {
  const docRef = doc(db, "NewsAndUpdates", "AppNewsAndUpdates"); // Firestore path
  try {
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Access fields 'title' and 'body' from the document
      const title = docSnap.data().title;
      const body = docSnap.data().body;

      // Return or log the values
      return { title, body };
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};


/**
 * Function to retrieve data from a specific document in Firestore
 * @returns {Promise<Object|null>} The document data or null if not found
 */
export const getUserProfile = async (userID) => {
  try {
    const docRef = doc(db, "UserProfiles", userID); // Path to the document
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      return docSnapshot.data(); // Return the document data
    } else {
      return null; // Return null if document doesn't exist
    }
  } catch (error) {
    throw error; // Handle errors
  }
};

/**
 * Fetches the adminGymID field for a user.
 * @param {string} userId - The user ID for which to fetch the adminGym field.
 * @returns {Promise<string|null>} - The adminGymID value or null if not found.
 */
export const getAdminGymID = async () => {
  try {
    let userID = await getCurrentUserUid();
    // Define the document path
    const userDocRef = doc(db, `UserProfiles/${userID}`);

    // Fetch the document snapshot
    const userDocSnapshot = await getDoc(userDocRef);

    if (userDocSnapshot.exists()) {
      // Extract and return the adminGym field
      const adminGym = userDocSnapshot.data().adminGymID;
      return adminGym || null;
    } else {
      console.warn(`No user document found for userId: ${userID}`);
      return null;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches the adminGym field for a user.
 * @param {string} userId - The user ID for which to fetch the adminGym field.
 * @returns {Promise<string|null>} - The adminGym value or null if not found.
 */
export const getAdminGym = async () => {
  try {
    let userID = await getCurrentUserUid();
    // Define the document path
    const userDocRef = doc(db, `UserProfiles/${userID}`);

    // Fetch the document snapshot
    const userDocSnapshot = await getDoc(userDocRef);

    if (userDocSnapshot.exists()) {
      // Extract and return the adminGym field
      const adminGym = userDocSnapshot.data().adminGym;
      return adminGym || null;
    } else {
      console.warn(`No user document found for userId: ${userID}`);
      return null;
    }
  } catch (error) {
    throw error;
  }
};

// --- Fetch Passcode from Firestore --- //
export const fetchAdminPasscode = async () => {
  try {
    let gymID = await getAdminGymID();
    let userID = await getCurrentUserUid();
    const docRef = doc(db, `GymProfiles/AllGyms/${gymID}/${userID}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().adminPasscode;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};


export const getAllEmployees = async () => {
  try {
    let userID = await getCurrentUserUid();
    let gymID = await getAdminGymID();
    const docRef = doc(db, `GymProfiles/AllGyms/${gymID}/${userID}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data().allEmployees;
    } else {
      return [];
    }
  }
  catch (error) {
    throw error;
  }
};

export const getEmployeeData = async (employeeNames) => {
  let userID = await getCurrentUserUid();
  let gymID = await getAdminGymID();
  const employeeDataPromises = employeeNames.map(async (employeeName) => {
    const employeeDocRef = doc(db, `GymProfiles/AllGyms/${gymID}/${userID}/EmployeeData/${employeeName}`);
    const employeeDocSnap = await getDoc(employeeDocRef);

    if (employeeDocSnap.exists()) {
      return { name: employeeName, ...employeeDocSnap.data() };
    } else {
      return { name: employeeName, error: "Employee data not found" };
    }
  });

  const employeeData = await Promise.all(employeeDataPromises);
  return employeeData;
};

/**
 * Add or update employee data in Firestore.
 * @param {object} employee - The employee data object.
 * @param {string} employee.id - The unique ID for the employee.
 * @param {string} employee.name - The name of the employee.
 * @param {string} employee.employeeCode - The employee's code.
 * @param {boolean} employee.status - The status of the employee (e.g., active/inactive).
 * @returns {Promise<void>} - Resolves when the operation is complete.
 */
export const addOrUpdateEmployee = async (employee) => {
  try {
    const gymID = await getAdminGymID();
    const userID = await getCurrentUserUid();
    const newID = employee.id || uuidv4();

    // Reference for the individual employee document
    const employeeRef = doc(
      db,
      `GymProfiles/AllGyms/${gymID}/${userID}/EmployeeData/${newID}`
    );

    // Reference for the allEmployees array
    const allEmployeesRef = doc(db, `GymProfiles/AllGyms/${gymID}/${userID}`);

    // Build the data object to set
    const employeeData = {
      ...(employee.name && { name: employee.name }), // Include if `name` exists
      ...(employee.employeeCode && { employeeCode: employee.employeeCode }), // Include if `employeeCode` exists
      ...(employee.status !== undefined && { status: employee.status }), // Include if `status` exists
      ...({ id: newID }),
    };

    // Add or update the employee data in Firestore
    await setDoc(employeeRef, employeeData, { merge: true }); // Merges with existing data if present

    // Add the employee's name to the allEmployees array
    await updateDoc(allEmployeesRef, {
      allEmployees: arrayUnion(newID),
    });

    showMessageWithDelay(
      "Employee Saved",
      "The data for " + employee.name + " has been successfully saved.",
      "success",
      3000
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Delete multiple employees from Firestore.
 * @param {string[]} employeeNames - An array of employee names to delete.
 * @returns {Promise<void>} - Resolves when all deletions are complete.
 */
export const deleteEmployees = async (employeeNames) => {
  try {
    const gymID = await getAdminGymID();
    const userID = await getCurrentUserUid();

    // Reference for the allEmployees array
    const allEmployeesRef = doc(db, `GymProfiles/AllGyms/${gymID}/${userID}`);

    // Batch to perform multiple deletions
    const batch = writeBatch(db);

    // Add delete operations for each employee
    employeeNames.forEach((employeeName) => {
      const employeeRef = doc(
        db,
        `GymProfiles/AllGyms/${gymID}/${userID}/EmployeeData/${employeeName}`
      );
      batch.delete(employeeRef);
    });

    // Commit the batch deletion
    await batch.commit();

    // Update the allEmployees array by removing all the employee names
    await updateDoc(allEmployeesRef, {
      allEmployees: arrayRemove(...employeeNames),
    });

    showMessageWithDelay(
      "Employees Deleted",
      `The data for ${employeeNames.length} employees has been successfully deleted.`,
      "success",
      3000
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Writes data to Firestore for an array of certification data.
 * @param {string} userID - The ID of the user.
 * @param {Array<Object>} certDataArray - An array of certification data objects. Each object should contain id and other data.
 * @param {Object} employee - The employee object containing employee details.
 * @returns {Promise<void>} - A promise that resolves when all data is written.
 */
export const addCertificationInfo = async (userID, certDataArray, employee) => {
  try {
    const userRef = doc(db, "UserProfiles", userID); // Reference to the user's profile document

    // Use Promise.all to handle multiple Firestore writes concurrently
    await Promise.all(certDataArray.map(async (certData) => {
      const { id, name } = certData;

      // Validate fields
      if (!id) {
        throw new Error(`Missing id for certification: ${JSON.stringify(certData)}`);
      }

      // Define the document path for certification info
      const certRef = doc(db, "UserProfiles", userID, "CertificationInfo", id);

      // Ensure id is passed as an array element to getCertificationsDetails
      const certInfo = await getCertificationsDetails([id]); // Wrap `id` in an array

      // Await the result of getAdminGym
      const gymName = await getAdminGym();

      // Write certification details to Firestore with merge option
      await setDoc(certRef, {
        employee: employee.name,
        endDate: getEndDateTimestamp(),
        gym: gymName || "Unknown Gym",
        id: id,
        startDate: getCurrentTimestamp(),
        title: name,
        type: certInfo[0].type || "Unknown Type",
      }, { merge: true });

      // Add the certification ID to the myCertifications array in the user's profile
      await updateDoc(userRef, {
        myCertifications: arrayUnion(id),
      });
    }));

  } catch (error) {
  }
};